import { z } from 'zod';

/**
 * RFC 9457 `application/problem+json`, the shape every api error takes
 * (ARCHITECTURE §3 API; `api/app/core/errors.py` and
 * `api/app/core/security/problems.py` produce them).
 *
 * The `type` member is a `urn:26ad:<slug>` URI, so a screen branches on the URN
 * rather than on a status code or a human-readable message — BP-01 R16's
 * `urn:26ad:tenant-readonly` is the one the `ReadOnlyBanner` reacts to.
 */

/** RFC 9457 §4.2.1: `type` when the status code says everything there is to say. */
export const DEFAULT_TYPE = 'about:blank';

/** The URN namespace of every 26AD problem type. */
export const PROBLEM_URN_PREFIX = 'urn:26ad:';

/**
 * Every problem type the api can answer a web request with, copied from
 * `api/app/core/errors.py` (`URN_*`) and `api/app/core/security/problems.py`
 * (the `slug=` of each factory).
 */
export const PROBLEM = {
  /** 401 — no usable credential was presented. */
  unauthenticated: 'urn:26ad:unauthenticated',
  /** 403 — authenticated, but the permission matrix says no. */
  forbidden: 'urn:26ad:forbidden',
  /** 403 — cookie auth on an unsafe method without a matching `X-CSRF` (R5). */
  csrfFailed: 'urn:26ad:csrf-failed',
  /** 404 — no tenant resolved for this host (R1). */
  noTenant: 'urn:26ad:no-tenant',
  /** 429 — the per-IP `/auth/*` budget is spent (BP-01 §9). */
  rateLimited: 'urn:26ad:rate-limited',
  /** 423 — `tenant.status = readonly` (R16). */
  tenantReadonly: 'urn:26ad:tenant-readonly',
  /** 401 — the session tenant disagrees with the `Host` header (R1). */
  tenantMismatch: 'urn:26ad:tenant-mismatch',
  /** 500 — a query ran with no tenant in scope (R2). */
  tenantContextMissing: 'urn:26ad:tenant-context-missing',
  /** 422 — Pydantic rejected the body or the query. */
  validationError: 'urn:26ad:validation-error',
  /** 500 — an unhandled exception reached the ASGI boundary. */
  internalError: 'urn:26ad:internal-error',
  /**
   * 426 — the installed build is older than `mobile.min_supported_version`
   * (BP-11 R3). Every route answers it, so the app has to handle it
   * centrally rather than screen by screen.
   */
  appUpdateRequired: 'urn:26ad:app-update-required',
  /**
   * Raised by this client, never by the api: a 2xx body that does not match the
   * OpenAPI contract the generated types were built from (ARCHITECTURE §3
   * Validation — Zod on the web from the same OpenAPI).
   */
  contractMismatch: 'urn:26ad:web-contract-mismatch',
} as const;

/** One RFC 9457 problem detail object. */
export interface Problem {
  /** `urn:26ad:<slug>`, or `about:blank` when the body carried no type. */
  type: string;
  title: string;
  status: number;
  detail: string | undefined;
  /**
   * RFC 9457 §3.2 extension members — everything the body carried beyond the
   * four above, verbatim.
   *
   * Kept because the api puts the actionable part of several problems there
   * and nowhere else: BP-06's `billing-settings-invalid` carries `errors[]`,
   * the per-field map §6 asks a settings screen to draw, and BP-01's own
   * validation handler uses the same shape. A screen that had only `detail`
   * would have to re-parse an English sentence to find out which field was
   * wrong.
   *
   * Optional on the interface and never optional on `ProblemError`: a hand-
   * written `Problem` in a test is describing a status and a URN, not a
   * body, and should not have to write `extensions: {}` to say so.
   */
  extensions?: Readonly<Record<string, unknown>>;
}

/** The three members RFC 9457 §3.1 requires us to be able to read back. */
const problemSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number(),
  detail: z.string().nullish(),
});

/**
 * Turn a response status and its parsed body into a `Problem`.
 *
 * A body that is not a problem document (an HTML error page from a proxy, an
 * empty 502, a `null` from a failed `response.json()`) still produces a usable
 * problem, typed `about:blank` — RFC 9457 §4.2.1's own answer for "the status
 * code is all there is".
 */
export function toProblem(status: number, body: unknown): Problem {
  const parsed = problemSchema.safeParse(body);
  if (!parsed.success) {
    return {
      type: DEFAULT_TYPE,
      title: `HTTP ${String(status)}`,
      status,
      detail: undefined,
      extensions: {},
    };
  }
  return {
    type: parsed.data.type,
    title: parsed.data.title,
    status: parsed.data.status,
    detail: parsed.data.detail ?? undefined,
    extensions: extensionsOf(body as object),
  };
}

/**
 * Everything in the body that is not one of RFC 9457's own four members.
 *
 * Called only after `problemSchema.safeParse` has succeeded, so `body` is a
 * non-null object — a Zod object schema rejects everything else, and the
 * caller has already returned the `about:blank` fallback for those. A guard
 * here would be a branch no input can reach.
 */
function extensionsOf(body: object): Record<string, unknown> {
  const rest: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(body as Record<string, unknown>)) {
    if (!RFC_MEMBERS.has(key)) {
      rest[key] = value;
    }
  }
  return rest;
}

/** The members RFC 9457 defines itself; everything else is an extension. */
const RFC_MEMBERS: ReadonlySet<string> = new Set(['type', 'title', 'status', 'detail', 'instance']);

/**
 * A `Problem` as a thrown error, so `try`/`catch` and TanStack Query's `error`
 * both carry the full document and a screen can branch on `type`.
 */
export class ProblemError extends Error {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly detail: string | undefined;
  readonly extensions: Readonly<Record<string, unknown>>;

  constructor(problem: Problem) {
    super(`${String(problem.status)} ${problem.type}: ${problem.title}`);
    this.name = 'ProblemError';
    this.type = problem.type;
    this.title = problem.title;
    this.status = problem.status;
    this.detail = problem.detail;
    this.extensions = problem.extensions ?? {};
  }

  /** True when this is the given `urn:26ad:*` problem type. */
  is(type: string): boolean {
    return this.type === type;
  }
}

/** Narrow an unknown thrown value (TanStack Query's `error`) to a `ProblemError`. */
export function isProblem(error: unknown): error is ProblemError {
  return error instanceof ProblemError;
}

/** True when `error` is the given `urn:26ad:*` problem type. */
export function isProblemType(error: unknown, type: string): boolean {
  return isProblem(error) && error.is(type);
}
